import {useCallback, useState} from 'react';

import {useI18n} from '@udemy/i18n';

import {createLearningReminder, syncReminderToGoogleCalendar} from '../api/learning-reminder-api';

import type {ReminderRecord} from '../api/calendar-export';
import type {GoogleTokenResponse} from '../api/google-calendar-auth-api';

export interface CreateReminderOptions {
    title: string;
    start: Date;
    end: Date;
    courseId?: string;
    courseUrl?: string;
}

export interface UseLearningReminderResult {
    reminder: ReminderRecord | null;
    isCreating: boolean;
    isSyncing: boolean;
    error: string | null;
    clearError: () => void;
    createReminder: (options: CreateReminderOptions) => Promise<boolean>;
    syncToGoogleCalendar: (requestToken: () => Promise<GoogleTokenResponse>) => Promise<boolean>;
}

export function useLearningReminder(): UseLearningReminderResult {
    const {gettext} = useI18n();
    const [reminder, setReminder] = useState<ReminderRecord | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const clearError = useCallback(() => setError(null), []);

    const createReminder = useCallback(
        async (options: CreateReminderOptions) => {
            setIsCreating(true);
            setError(null);
            try {
                const created = await createLearningReminder(options);
                setReminder(created);
                return true;
            } catch {
                setError(gettext('Something went wrong. Please try again.'));
                return false;
            } finally {
                setIsCreating(false);
            }
        },
        [gettext],
    );

    const syncToGoogleCalendar = useCallback(
        async (requestToken: () => Promise<GoogleTokenResponse>) => {
            if (!reminder) return false;
            setIsSyncing(true);
            setError(null);
            try {
                const tokenResponse = await requestToken();
                await syncReminderToGoogleCalendar(reminder, tokenResponse);
                return true;
            } catch (e) {
                const errorType = (e as Error).message;
                // popup_closed = user dismissed voluntarily; not an error worth surfacing
                if (errorType !== 'popup_closed') {
                    setError(gettext('Something went wrong. Please try again.'));
                }
                return false;
            } finally {
                setIsSyncing(false);
            }
        },
        [reminder, gettext],
    );

    return {
        reminder,
        isCreating,
        isSyncing,
        error,
        clearError,
        createReminder,
        syncToGoogleCalendar,
    };
}
